import React, { ComponentProps, FormEvent, useCallback, useEffect, useMemo, useRef } from "react";

import {
	Dialog,
	DialogButton,
	DialogButtonHandle,
	DialogContent,
	DialogFooter,
	DialogTitle,
} from "../../dialog";
import { Modal } from "../../modal";
import { TextField } from "../../text-field";

import "./save-dialog.css";

export const SaveDialog = ({
	initialName = "",
	nameMaxLength,
	onSave,
	onDontSave,
	inputLabel,
	messageHtml,
	save,
	dontSave,
	title,
	fullScreen,
	...modalProps
}: {
	initialName?: string;
	nameMaxLength: number;
	inputLabel?: string;
	messageHtml?: string;
	save?: string;
	dontSave?: string;
	title?: string;
	onSave: (name: string) => void;
	onDontSave: () => void;
	onBackToProduct?: () => void;
	fullScreen?: boolean;
} & ComponentProps<typeof Modal>) => {
	const valueRef = useRef<string>(initialName);
	const buttonRef = useRef<DialogButtonHandle>(null);
	const textFieldRef = useRef<HTMLInputElement>(null);

	const handleOnOk = useCallback(() => {
		if (valueRef.current) {
			onSave(valueRef.current);
		}
	}, [onSave, valueRef]);

	useEffect(() => {
		const el = textFieldRef.current;
		if (!el) return;
		el.setSelectionRange(0, initialName.length);
	}, [initialName.length]);

	const handleOnSubmit = useCallback(
		(e: FormEvent) => {
			e.preventDefault();

			if (valueRef.current) {
				onSave(valueRef.current.substring(0, nameMaxLength));
			}
		},
		[valueRef, onSave, nameMaxLength],
	);

	const handleOnChange = useCallback(
		(e: FormEvent<HTMLInputElement>) => {
			const newName = e.currentTarget.value;
			valueRef.current = newName;
			if (buttonRef.current) {
				buttonRef.current.toggleDisable(!newName || newName.length > nameMaxLength);
			}
		},
		[nameMaxLength],
	);

	const Footer = useMemo(
		() =>
			function SaveDialogFooter() {
				return (
					<DialogFooter>
						<DialogButton
							type="button"
							onClick={handleOnOk}
							ref={buttonRef}
							disabled={!initialName || initialName.length > nameMaxLength}
						>
							{save}
						</DialogButton>
						<DialogButton type="button" onClick={onDontSave}>
							{dontSave}
						</DialogButton>
					</DialogFooter>
				);
			},
		[dontSave, handleOnOk, initialName, nameMaxLength, onDontSave, save],
	);

	return (
		<Modal {...modalProps}>
			<Dialog
				fullScreen={!!fullScreen}
				className="save-dialog"
				onCloseIcon={fullScreen ? onDontSave : undefined}
				{...modalProps}
			>
				<DialogTitle>{title}</DialogTitle>
				<DialogContent>
					<div className="save-dialog__message">{messageHtml}</div>
					{inputLabel ? (
						<div className="save-dialog__input-label">{inputLabel}</div>
					) : null}
					<form onSubmit={handleOnSubmit}>
						<TextField
							ref={textFieldRef}
							defaultValue={initialName.trim()}
							onChange={handleOnChange}
							isFullWidth
							maxLength={nameMaxLength}
							// eslint-disable-next-line jsx-a11y/no-autofocus
							autoFocus
						/>
					</form>
				</DialogContent>
				<Footer />
			</Dialog>
		</Modal>
	);
};

SaveDialog.displayName = "SaveDialog";
